import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Link2, Unlink, Check, Pause, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { Field, TableWrap, Empty, th, td, tr } from "@/components/common";

export const FLAG = {
  duplicate: ["Duplicate Check", "bg-red-100 text-red-700"], already_imported: ["Already Imported", "bg-orange-100 text-orange-800"],
  free_pass: ["₹0 / Free Pass", "bg-sky-100 text-sky-800"], bad_date: ["Date unreadable", "bg-amber-100 text-amber-800"],
  topup_linked: ["Bank Linked", "bg-emerald-100 text-emerald-800"], topup_unlinked: ["Bank link pending", "bg-amber-100 text-amber-800"],
};
export const POSTING = {
  trip: ["Trip Toll", "bg-emerald-100 text-emerald-800"], vehicle: ["Unallocated (Vehicle)", "bg-amber-100 text-amber-800"], unmatched: ["Vehicle not in Fleet", "bg-red-100 text-red-700"],
  review: ["Held for Review", "bg-red-100 text-red-700"], free_pass: ["₹0 Pass", "bg-sky-100 text-sky-800"], dispute: ["Bank Dispute", "bg-violet-100 text-violet-800"],
  hold: ["On Hold", "bg-amber-100 text-amber-800"], recovery: ["Recovery Ledger", "bg-fuchsia-100 text-fuchsia-800"], ignored: ["Ignored (no deduction)", "bg-slate-200 text-slate-700"], wallet_credit: ["Wallet Credit", "bg-emerald-100 text-emerald-800"],
};
const dt = (s) => s?.replace("T", " ").slice(0, 19);
const Pill = ({ m }) => (m ? <span className={`mr-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${m[1]}`}>{m[0]}</span> : null);
export const Tags = ({ r }) => <>{(r.flags || []).map((f) => <Pill key={f} m={FLAG[f]} />)}</>;

const ACTS = [["approve", "Approve", Check, "default"], ["hold", "Hold", Pause, "outline"], ["claim", "Claim", Undo2, "outline"]];

function ClaimDialog({ row, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const save = async () => { try { await api.post(`/fastag/txns/${row.id}/route`, { action: "claim", note }); toast.success("Claim raised"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card" data-testid="fastag-claim-dialog">
        <DialogHeader><DialogTitle>Bank Claim — {row.vehicle_number || row.tag_id} · {inr(row.amount, 2)}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{dt(row.crossed_at)} · {row.plaza} · Txn {row.txn_id}</p>
        <Field label="Reason / complaint no."><Input data-testid="claim-note" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <DialogFooter><Button data-testid="claim-save" onClick={save}>Raise Claim</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RowActions({ r, onChanged, onClaim }) {
  const go = async (action) => { try { await api.post(`/fastag/txns/${r.id}/route`, { action }); toast.success(action === "approve" ? "Approved — added to trip by date" : action === "hold" ? "On hold" : "Claim settled"); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  if (r.direction !== "debit") return null;
  if (r.review_action === "claim") {
    return r.claim_status === "settled" ? <span className="text-xs font-semibold text-emerald-700" data-testid={`ft-claim-settled-${r.id}`}>Refund received</span>
      : <Button size="sm" variant="outline" data-testid={`ft-settle-${r.id}`} onClick={() => go("settle")}>Mark Refund Received</Button>;
  }
  return (
    <div className="flex gap-1">{ACTS.filter(([a]) => a !== r.review_action && !(a === "claim" && r.amount <= 0)).map(([a, label, Icon, v]) => (
      <Button key={a} size="sm" variant={v} data-testid={`ft-${a}-${r.id}`} onClick={() => (a === "claim" ? onClaim(r) : go(a))}><Icon className="mr-1 h-3.5 w-3.5" />{label}</Button>))}</div>
  );
}

export function EntryTable({ rows, onChanged, showBalance }) {
  const [claim, setClaim] = useState(null);
  return (
    <>
      <TableWrap>
        <thead><tr>{["#", "Date & Time", "Txn ID", "Vehicle", "Toll Plaza", "Dr/Cr", "Amount", ...(showBalance ? ["Balance"] : []), "Tags", "Posted To", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.id} className={`${tr} ${r.review_status === "pending" ? "bg-red-50/60 dark:bg-red-950/20" : r.review_status === "hold" ? "bg-amber-50/70 dark:bg-amber-950/20" : ""}`} data-testid={`ft-entry-${r.id}`}>
            <td className={`${td} text-xs text-muted-foreground`}>{(r.seq ?? i) + 1}</td><td className={`${td} whitespace-nowrap`}>{dt(r.crossed_at)}</td>
            <td className={`${td} font-mono text-xs`}>{r.txn_id}</td><td className={`${td} font-mono`}>{r.vehicle_number || r.tag_id || "-"}</td><td className={td}>{r.plaza || "-"}</td>
            <td className={td}>{r.direction === "credit" ? "Cr" : "Dr"}</td><td className={`${td} amount`}>{inr(r.amount, 2)}</td>
            {showBalance && <td className={`${td} amount text-xs`}>{r.balance != null ? inr(r.balance, 2) : ""}</td>}
            <td className={td}><Tags r={r} /></td>
            <td className={td}><Pill m={POSTING[r.posting]} />{r.lr_number && <Link to={`/bookings/${r.booking_id}`} className="font-mono text-xs text-primary">{r.lr_number}</Link>}{r.recovery_from && <span className="text-xs"> {r.recovery_from}</span>}{r.review_action === "claim" && <span className="text-xs"> {r.claim_status === "settled" ? "Settled" : "Open"}{r.review_note ? ` · ${r.review_note}` : ""}</span>}</td>
            <td className={td}><RowActions r={r} onChanged={onChanged} onClaim={setClaim} /></td>
          </tr>))}</tbody>
      </TableWrap>
      {claim && <ClaimDialog row={claim} onClose={() => setClaim(null)} onSaved={onChanged} />}
    </>
  );
}

export function ReviewQueue({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [all, setAll] = useState(false);
  const load = () => { onChanged?.(); return api.get(`/fastag/review${all ? "?all=true" : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); };
  useEffect(() => { load(); }, [all]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rows) return null;
  return (
    <div data-testid="fastag-review">
      <p className="mb-3 text-sm text-muted-foreground">Duplicate, ₹0 / Free Pass, pehle import ho chuki aur galat date wali entries yahan statement ke kram me dikhti hain. Laal line = review baaki, peeli = Hold. Approve karte hi toll trip ki Loading–Delivery date ke hisaab se apne aap trip kharche me judta hai (trip na ho to gaadi ke kharche me). Hold = kharcha nahi banta, queue me rukta hai. Claim = bank se paisa wapas maangna; refund aane par "Mark Refund Received".</p>
      <label className="mb-3 flex items-center gap-2 text-sm"><input type="checkbox" data-testid="review-show-resolved" checked={all} onChange={(e) => setAll(e.target.checked)} />Approved / settled entries bhi dikhayein</label>
      <EntryTable rows={rows} onChanged={load} />
      {!rows.length && <Empty testid="fastag-review-empty" text="Review ke liye koi entry nahi" />}
    </div>
  );
}

export function StatementDialog({ imp, onClose }) {
  const [d, setD] = useState(null);
  const load = () => api.get(`/fastag/imports/${imp.id}/statement`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [imp.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-[96vw] overflow-y-auto bg-card" data-testid="fastag-statement-dialog">
        <DialogHeader><DialogTitle>Statement — {imp.file_name} ({d?.rows.length ?? "…"} rows, file ke kram me)</DialogTitle></DialogHeader>
        {d && <EntryTable rows={d.rows} onChanged={load} showBalance />}
      </DialogContent>
    </Dialog>
  );
}

export function TopUps() {
  const [d, setD] = useState(null);
  const load = () => api.get("/fastag/topups").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const act = async (url, body, msg) => { try { await api.post(url, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  return (
    <div className="space-y-4" data-testid="fastag-topups">
      <p className="text-sm text-muted-foreground">Bank statement ya manual entry me "FASTag / NETC" wala debit aate hi FASTag Wallet me apne aap credit ho jaata hai aur dono entries jud jaati hain. Jo apne aap nahi pehchaane gaye, unhe neeche se "Link" karein.</p>
      <h3 className="text-sm font-bold">Wallet Credits (Recharges)</h3>
      <TableWrap>
        <thead><tr>{["Date", "Amount", "Wallet Entry", "Bank Debit (source)", "Statement Row", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{d.credits.map((c) => <tr key={c.id} className={tr} data-testid={`topup-${c.id}`}>
          <td className={td}>{fmtDate(c.date)}</td><td className={`${td} amount font-semibold`}>{inr(c.amount, 2)}</td><td className={`${td} text-xs`}>{c.description}</td>
          <td className={`${td} text-xs`}>{c.source ? <span className="text-emerald-700">{c.source.bank_name} · {fmtDate(c.source.date)} · {c.source.description}</span> : <span className="font-semibold text-amber-700">Not linked</span>}</td>
          <td className={`${td} text-xs`}>{c.statement_txn_id ? "Matched" : "-"}</td>
          <td className={td}>{c.source && <Button size="sm" variant="outline" title="Unlink from bank" data-testid={`topup-unlink-${c.id}`} onClick={() => act(`/fastag/topups/${c.id}/unlink`, {}, "Unlinked")}><Unlink className="mr-1 h-3.5 w-3.5" />Unlink</Button>}</td>
        </tr>)}</tbody>
      </TableWrap>
      {!d.credits.length && <p className="text-sm text-muted-foreground">Abhi koi recharge nahi.</p>}
      <h3 className="text-sm font-bold">Unreconciled Bank Debits (FASTag jaise upar)</h3>
      <TableWrap>
        <thead><tr>{["Date", "Bank", "Description", "Amount", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{d.candidates.slice(0, 50).map((c) => <tr key={c.id} className={tr} data-testid={`topup-cand-${c.id}`}>
          <td className={td}>{fmtDate(c.date)}</td><td className={td}>{c.bank_name}</td><td className={`${td} text-xs`}>{c.description}</td><td className={`${td} amount`}>{inr(c.amount, 2)}</td>
          <td className={td}><Button size="sm" data-testid={`topup-link-${c.id}`} onClick={() => act("/fastag/topups/link", { bank_txn_id: c.id }, "Linked — FASTag Wallet credited")}><Link2 className="mr-1 h-3.5 w-3.5" />Link as Recharge</Button></td>
        </tr>)}</tbody>
      </TableWrap>
    </div>
  );
}
